// Shared, pure last-name comparator for the Speakers surfaces
// (SpeakersSection on the homepage + /speakers full page).
//
// SCOPE LOCK (Round 2, 2C): display-time sort only. This module never
// writes to the DB, never mutates the input array, and never touches
// the manual `order` field. It is imported by both surfaces so the
// sort logic exists in exactly one place.
//
// Behavioural rules — mirrors the approved product decision:
//   • Trim + collapse whitespace in the raw name.
//   • Strip leading honorifics (Dr., Mr., Mrs., Ms., Prof., Sir,
//     Lady, Lord, Rabbi, Fr., Rev., Hon.) — case-insensitive, with
//     or without trailing period.
//   • Strip trailing suffixes (Jr., Sr., II, III, IV, PhD, MD, Esq.)
//     — case-insensitive, tolerant of trailing commas and periods.
//   • Preserve hyphens (Al-Farouq) and apostrophes (O'Neill) inside
//     name tokens verbatim.
//   • Treat common surname particles (de, del, la, van, von, da) as
//     part of the surname block — walking right-to-left from the
//     final token, absorb any consecutive lowercase particle tokens
//     into the surname (e.g. "Alicia Del Gallo" → "del gallo";
//     "Ana de la Cruz" → "de la cruz").
//   • "Lady Comfort" → "Lady" is stripped as honorific, single-token
//     name "Comfort" → sort key = "comfort".
//   • A true single-token name sorts using that token.
//   • An empty / unusable name sorts AFTER named speakers (still
//     rendered by the caller — this comparator does not filter).
//   • Compare with a fixed English collator, case-insensitive
//     (Intl.Collator { locale: 'en', sensitivity: 'base' }).
//   • Tie-break 1: normalized full name (case-insensitive).
//   • Tie-break 2: the record's stable `id` field.
//
// The list of honorifics/suffixes/particles is intentionally
// conservative and product-approved. Extending it later should be
// done here in one place.

const TITLES = new Set([
  'dr', 'dr.',
  'mr', 'mr.',
  'mrs', 'mrs.',
  'ms', 'ms.',
  'miss',
  'prof', 'prof.', 'professor',
  'sir',
  'dame',
  'lady',
  'lord',
  'rabbi',
  'fr', 'fr.', 'father',
  'rev', 'rev.', 'reverend',
  'hon', 'hon.', 'honorable',
  'coach',
]);

const SUFFIXES = new Set([
  'jr', 'jr.',
  'sr', 'sr.',
  // Roman numeral generation suffixes II–X. "I" is intentionally
  // excluded (ambiguous with the pronoun / single-letter tokens).
  // These are only stripped when they follow a MULTI-TOKEN name —
  // see the loop guard `tokens.length > 1` below. A genuinely single-
  // token name such as "V" or "X" is preserved as-is.
  'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
  'phd', 'ph.d', 'ph.d.',
  'md', 'm.d', 'm.d.',
  'esq', 'esq.',
]);

const PARTICLES = new Set([
  'de', 'del', 'la',    // Spanish / Italian
  'van', 'von', 'da',   // Dutch / German / Portuguese
]);

// A fixed English collator, created once. sensitivity:'base' folds
// case AND accent for the primary comparison — the tie-break by
// normalized full name (also case-folded) then by `id` guarantees a
// stable, deterministic order.
const COLLATOR = new Intl.Collator('en', {
  sensitivity: 'base',
  usage: 'sort',
  numeric: false,
  caseFirst: 'false',
});

// Normalise raw name: trim, collapse internal whitespace runs.
export function normalizeName(name) {
  if (name === null || name === undefined) return '';
  return String(name).trim().replace(/\s+/g, ' ');
}

// Strip a single trailing comma or period from a token — used only
// to check title / suffix membership; the original tokens keep their
// original punctuation.
const strip = (t) => t.replace(/[.,]+$/g, '');

// Given a normalized name, return the lowercase sort key for last-
// name ordering. See rules in the file header.
export function getLastNameKey(rawName) {
  const name = normalizeName(rawName);
  if (!name) return '';

  let tokens = name.split(' ');

  // Strip leading honorifics (repeatable — e.g. "Rev. Dr. Alice X.")
  while (tokens.length > 0 && TITLES.has(tokens[0].toLowerCase())) {
    tokens.shift();
  }

  // Strip trailing suffixes (repeatable — e.g. "Alice X. Jr., PhD").
  // NOTE: guarded by `tokens.length > 1` so a genuinely single-token
  // name (e.g. "V", "X", "Cher") is NEVER reduced to empty. This
  // matters for Roman-numeral suffixes VI–X which look like names by
  // themselves — the guard preserves true single-token names.
  while (
    tokens.length > 1 &&
    SUFFIXES.has(strip(tokens[tokens.length - 1]).toLowerCase())
  ) {
    tokens.pop();
  }

  if (tokens.length === 0) {
    // All tokens were titles/suffixes — fall back to normalised name.
    return name.toLowerCase();
  }
  if (tokens.length === 1) {
    return tokens[0].toLowerCase();
  }

  // Walk right-to-left, absorbing consecutive surname particles into
  // the surname block. Only tokens that appear lowercase-normalised
  // in the PARTICLES set are considered particles.
  const surname = [tokens[tokens.length - 1]];
  for (let i = tokens.length - 2; i >= 0; i--) {
    if (PARTICLES.has(tokens[i].toLowerCase())) {
      surname.unshift(tokens[i]);
    } else {
      break;
    }
  }

  return surname.join(' ').toLowerCase();
}

// Factory — returns a pure comparator suitable for `Array.prototype.sort`.
// The same instance is reused inside a single sort call to keep the
// collator hot.
export function makeSpeakerComparator() {
  return function speakerCompare(a, b) {
    const nameA = normalizeName(a && a.name);
    const nameB = normalizeName(b && b.name);

    // Empty / unusable names sort AFTER all named speakers, but
    // remain in the array (the caller still renders them).
    if (!nameA && !nameB) {
      return COLLATOR.compare(String((a && a.id) || ''), String((b && b.id) || ''));
    }
    if (!nameA) return 1;
    if (!nameB) return -1;

    const keyA = getLastNameKey(nameA);
    const keyB = getLastNameKey(nameB);
    let c = COLLATOR.compare(keyA, keyB);
    if (c !== 0) return c;

    // Tie-break 1: full normalised name (case-folded)
    c = COLLATOR.compare(nameA.toLowerCase(), nameB.toLowerCase());
    if (c !== 0) return c;

    // Tie-break 2: stable unique id — the real record key.
    return COLLATOR.compare(String((a && a.id) || ''), String((b && b.id) || ''));
  };
}

// Pure, non-mutating alphabetiser. Returns a NEW array — the input
// array is never sorted in place, so React props / API responses are
// safe from side-effects.
export function sortSpeakersByLastName(speakers) {
  if (!Array.isArray(speakers) || speakers.length < 2) {
    return Array.isArray(speakers) ? speakers.slice() : [];
  }
  return speakers.slice().sort(makeSpeakerComparator());
}
